/**
 * Action-row anchor grammar (UX-13 S3/S4) — the detail pages' Call /
 * Email / Website actions are LINKS, and the Button primitive is
 * <button>-only by contract, so anchors carry Button's exact ruled
 * grammar (geometry ruling §12: rounded-base, 4px/14px density, the
 * invisible hit-area extension). Keep in lockstep with
 * primitives/Button.tsx BASE/VARIANT/SIZE classes.
 */

export const ACTION_ANCHOR_BASE =
  'relative inline-flex items-center justify-center gap-2 rounded-base font-semibold no-underline ' +
  "before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] " +
  'transition-all duration-[var(--dur-base)] focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-sage-400 min-h-[30px] px-[14px] py-1 text-sm';

export const ACTION_ANCHOR_PRIMARY =
  `${ACTION_ANCHOR_BASE} bg-sage-800 text-cream shadow-sh1 hover:bg-forest-700 hover:shadow-sh2`;

export const ACTION_ANCHOR_SECONDARY =
  `${ACTION_ANCHOR_BASE} bg-paper text-sage-800 border border-sage-300 hover:border-sage-400 hover:bg-sage-800/[6%]`;

/**
 * On-dark anchor grammar (Directory detail rebuild) — the Call / Email
 * actions on the FOREST band. Mirrors Button's `primary-on-dark` /
 * `secondary-on-dark` ladder (cream fill + forest text for the ONE primary;
 * cream-bordered ghost beneath). Gold is NEVER a fill here (design-language
 * §3: gold stays a non-interactive accent in bands). Keep in lockstep with
 * primitives/Button.tsx VARIANT_CLASSES.
 */
export const ACTION_ANCHOR_ON_DARK_PRIMARY =
  `${ACTION_ANCHOR_BASE} bg-cream text-sage-800 shadow-sh1 hover:bg-paper hover:shadow-sh2`;

export const ACTION_ANCHOR_ON_DARK_SECONDARY =
  `${ACTION_ANCHOR_BASE} bg-transparent text-cream border border-cream/40 hover:bg-cream/10 hover:border-cream/70`;

/**
 * The gold-punctuation CTA (§6, the ruled treatment that replaced the
 * retired gold slab + pale wash): paper ground, sage-800 text, warm-400/40
 * hairline, the gold at full strength confined to the small leading icon
 * chip. The icon chip itself is the composer's (needs its glyph).
 */
export const GOLD_PUNCTUATION_CTA =
  "relative inline-flex min-h-[30px] items-center gap-2 rounded-base border border-warm-400/40 bg-paper px-[14px] py-1 text-sm font-semibold text-sage-800 shadow-sh1 transition-all duration-[var(--dur-base)] ease-quiet hover:bg-warm-50 hover:shadow-sh2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']";
